import {
  Bone,
  FieldSkeleton,
  HeaderSkeleton,
  Line,
  pick,
  placeholderKeys,
  SkeletonRegion,
  TableSkeleton,
  type ColumnBone,
} from '@ems/ui/patterns/skeleton-blocks'

import { FIELD_GRID_CLASS, ROW_SPAN_CLASS } from '#admin/features/cms/field-layout.ts'

const DOCUMENT_COLUMNS: readonly ColumnBone[] = [
  { cell: 'text', widths: ['w-40', 'w-56', 'w-32'], primary: true, truncate: true, size: 3 },
  { cell: 'mono', widths: ['w-28', 'w-36', 'w-20'], truncate: true, size: 2 },
  { cell: 'badge', widths: ['w-28', 'w-20'] },
]

const COLLECTION_KEYS = placeholderKeys('collection', 3)

export function ContentIndexSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-8">
      <HeaderSkeleton title="w-28" description="w-80" actions={['h-8 w-36']} />
      {COLLECTION_KEYS.map((key, at) => (
        <TableSkeleton
          key={key}
          rows={at === 0 ? 4 : 2}
          card={{
            title: pick(['w-32', 'w-24', 'w-40'], at),
            description: 'w-20',
            action: 'h-8 w-28',
          }}
          columns={DOCUMENT_COLUMNS}
        />
      ))}
    </SkeletonRegion>
  )
}

const CHIP_KEYS = placeholderKeys('chip', 6)
const CHIP_WIDTHS = ['w-16', 'w-24', 'w-20', 'w-28'] as const

function GroupSkeleton({ title, tall }: { title: string; tall: boolean }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="border-b pb-1.5">
        <Line size="base" width={title} />
      </div>
      <div className={FIELD_GRID_CLASS}>
        <div className={ROW_SPAN_CLASS.cell}>
          <FieldSkeleton className="gap-1.5" label="w-20" />
        </div>
        <div className={ROW_SPAN_CLASS.cell}>
          <FieldSkeleton className="gap-1.5" label="w-28" />
        </div>
        <div className={ROW_SPAN_CLASS.full}>
          <FieldSkeleton
            className="gap-1.5"
            label="w-24"
            control={tall ? 'richtext-tall' : 'textarea'}
          />
        </div>
        <div className={ROW_SPAN_CLASS.full}>
          <FieldSkeleton className="gap-1.5" label="w-32" control="richtext" />
        </div>
      </div>
    </div>
  )
}

export function DocumentEditorSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-4">
      <HeaderSkeleton
        title="w-64"
        description="w-96"
        actions={['h-9 w-40', 'h-9 w-24', 'h-9 w-20']}
      />
      <div className="@container/fields flex flex-col gap-4">
        <div className="-mx-1">
          <ul className="bg-muted/50 flex gap-1.5 overflow-hidden px-1 py-2">
            {CHIP_KEYS.map((key, at) => (
              <li key={key} className="shrink-0">
                <Bone className={`h-8 ${pick(CHIP_WIDTHS, at)}`} />
              </li>
            ))}
          </ul>
        </div>
        <div className="flex min-w-0 flex-col gap-6">
          <GroupSkeleton title="w-20" tall={false} />
          <GroupSkeleton title="w-28" tall />
        </div>
      </div>
      <div className="@container/schedule flex flex-col gap-3 rounded-lg border p-3">
        <div className="border-b pb-1.5">
          <Line size="base" width="w-20" />
        </div>
        <div className="flex flex-col gap-1.5">
          <Line size="xs" width="w-24" className="h-3.5" />
          <div className="flex flex-col gap-2 @md/schedule:flex-row">
            <Bone className="h-9 min-w-0 flex-1" />
            <Bone className="h-9 w-24" />
          </div>
          <Line size="xs" width="w-72" />
        </div>
      </div>
      <TableSkeleton
        rows={3}
        card={{ title: 'w-16', description: 'w-96' }}
        columns={[
          { cell: 'text', widths: ['w-10', 'w-8'], primary: true },
          { cell: 'muted', widths: ['w-32', 'w-28'] },
          { cell: 'mono', widths: ['w-28', 'w-36'], truncate: true, size: 2 },
          { cell: 'muted', widths: ['w-40', 'w-24'], truncate: true, size: 3 },
          { cell: 'button', widths: ['w-20'], align: 'end', header: null },
        ]}
      />
    </SkeletonRegion>
  )
}
